import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import type { WhoamiResponse } from "@layer/dashboard/types";
import {
  pageAllowed,
  pageForRoute,
} from "@layer/dashboard/page-registry";
import { api, ApiError } from "../lib/api";
import { supabase } from "../lib/supabase";
import { setStudioTimeZone, studioTimeZone } from "../lib/studio-tz";
import { setCancellationDeadlineHours } from "../lib/studio-policy";
import { clearCache } from "../lib/resource-cache";
import { RealtimeProvider } from "../lib/realtime";

const STALE_SESSION_CODES = new Set([
  "studio_not_found",
  "studio_stale_session",
  "studio_unresolved",
]);

interface WhoamiState {
  readonly loading: boolean;
  readonly data: WhoamiResponse | null;
  readonly error: string | null;
}

/**
 * The viewer's effective page grant, folded from whoami's two fields into the
 * one thing every consumer here actually asks.
 *
 * `page_access_resolved: false` means the server could not tell who the viewer
 * is. Fail-closed by Mario's decision, so that collapses to the EMPTY grant —
 * Home and nothing else — rather than to `null` (unrestricted). Getting this
 * fold backwards is the single line that would turn the whole feature into a
 * no-op, which is why it is one named function with this comment on it.
 */
function effectiveGrant(
  data: WhoamiResponse | null,
): ReadonlyArray<string> | null {
  if (data === null) return null;
  if (!data.page_access_resolved) return [];
  return data.visible_pages;
}

interface NavItem {
  readonly to: string;
  readonly label: string;
  /** `/` matches every route unless the NavLink is told to match exactly. */
  readonly end?: boolean;
}

/**
 * Migration 178 — which nav entries this viewer may see.
 *
 * DERIVED from the shared registry rather than a second list of ids beside the
 * nav: `pageForRoute` maps the link's own `to` back to its page, so a nav
 * entry cannot drift out of step with the gate that will refuse its endpoint.
 * A link whose route belongs to no page is KEPT — the registry-completeness
 * test makes that state unreachable, and hiding a link on a technicality would
 * be a worse failure than showing one.
 *
 * `granted === null` is unrestricted and returns the list untouched, which is
 * every viewer's state until someone restricts them.
 */
function visibleNav(
  items: ReadonlyArray<NavItem>,
  granted: ReadonlyArray<string> | null,
): ReadonlyArray<NavItem> {
  if (granted === null) return items;
  return items.filter((n) => {
    const page = pageForRoute(n.to);
    return page === null ? true : pageAllowed(granted, page.id);
  });
}

// Primary nav — flush-left, grouped by what the page is FOR, with a rule
// between groups. The groups are the structure, not decoration: overview,
// then the two things that change hour to hour, then the studio's own work
// queues, then the read-and-configure surfaces. The three operator-of-Layer
// queues (PR, Inbox, Decisions) are NOT here: on the Clavis demo the top bar
// is the studio-operator set, so they live behind "More" (clavis-demo,
// 2026-09-14) — routes and page gates untouched.
const PRIMARY_NAV_GROUPS: ReadonlyArray<ReadonlyArray<NavItem>> = [
  [{ to: "/", label: "Home", end: true }],
  [
    { to: "/calendar", label: "Calendar" },
    { to: "/members", label: "Members" },
  ],
  [
    { to: "/tasks", label: "Tasks" },
    { to: "/flows", label: "Flows" },
    { to: "/automations", label: "Automations" },
    { to: "/agents", label: "Agents" },
  ],
  [
    { to: "/analytics", label: "Analytics" },
    { to: "/assets", label: "Assets" },
    { to: "/data", label: "Data" },
    { to: "/config", label: "Config" },
  ],
];

const PRIMARY_NAV: ReadonlyArray<NavItem> = PRIMARY_NAV_GROUPS.flat();

// Everything else lives behind the "More" dropdown, which sits immediately
// after the last primary link rather than pinned to the far edge. The three
// operator queues moved out of the bar come first, then the periodic
// surfaces, then the operator tools.
const MORE_NAV: ReadonlyArray<NavItem> = [
  { to: "/pr", label: "PR" },
  { to: "/inbox", label: "Inbox" },
  { to: "/decisions", label: "Decisions" },
  { to: "/rota", label: "Rota" },
  { to: "/payroll", label: "Payroll" },
  { to: "/reports", label: "Reports" },
  { to: "/status", label: "Status" },
  { to: "/errors", label: "Errors" },
  { to: "/email-templates", label: "Emails" },
  { to: "/efficiency", label: "Efficiency" },
  { to: "/preload", label: "Preload" },
  { to: "/catalog", label: "Catalog" },
  { to: "/cli", label: "CLI" },
  { to: "/mcp", label: "MCP" },
  { to: "/whatsapp-signup", label: "WA signup" },
];

// ─── Mobile navigation ──────────────────────────────────────────────────
// On phones the desktop bar (the primary links + a "More" dropdown) is replaced by
// three equal-width tabs: Calendar, Members, and "More" — where "More" is a
// full-screen sheet holding EVERYTHING else (every other primary link, the
// desktop More items, plus the signed-in identity + Logout). These two tabs
// are the direct destinations; the rest funnel through the sheet.
const MOBILE_TAB_TARGETS = new Set(["/calendar", "/members"]);
const MOBILE_MORE_NAV: ReadonlyArray<NavItem> = [
  ...PRIMARY_NAV.filter((n) => !MOBILE_TAB_TARGETS.has(n.to)),
  ...MORE_NAV,
];

/**
 * Refuses to render a page the viewer was not granted (migration 178).
 *
 * Two outcomes, deliberately different:
 *
 *   • RESTRICTED — they are simply not granted this page. Redirect to `/`
 *     (always allowed) with `replace`, so the Back button does not bounce them
 *     between the blocked URL and Home forever.
 *   • UNRESOLVED — we could not tell who they are. That is a broken
 *     `login_email` binding (migration 177), NOT a permissions decision, and
 *     telling someone "you don't have access" when the truth is "we don't know
 *     who you are" sends them to the wrong person for help. So this case gets a
 *     message naming the real problem and where it is fixed.
 *
 * A route belonging to no page renders — the registry-completeness test makes
 * that unreachable, and blocking on a technicality would be the worse failure.
 */
function RouteGuard({
  grant,
  resolved,
  children,
}: {
  readonly grant: ReadonlyArray<string> | null;
  readonly resolved: boolean;
  readonly children: React.ReactNode;
}): JSX.Element {
  const location = useLocation();
  const page = pageForRoute(location.pathname);
  if (page === null || pageAllowed(grant, page.id)) {
    return <>{children}</>;
  }
  if (!resolved) {
    return (
      <div className="page-blocked">
        <h1 className="page-title">We can&apos;t tell who you are</h1>
        <p>
          Your login isn&apos;t linked to a staff record, so the dashboard
          can&apos;t work out which pages you should see and is showing you none
          of them. This is a setup gap, not a permission you&apos;re missing.
        </p>
        <p className="subtle">
          Ask an admin to open <strong>Config → Staff allowlist</strong> and put
          your sign-in address in the <strong>login email</strong> column for
          your row.
        </p>
      </div>
    );
  }
  return <Navigate to="/" replace />;
}

const WhoamiContext = createContext<WhoamiState>({
  loading: true,
  data: null,
  error: null,
});

export function useWhoami(): WhoamiState {
  return useContext(WhoamiContext);
}

export function DashboardLayout(): JSX.Element {
  const [state, setState] = useState<WhoamiState>({
    loading: true,
    data: null,
    error: null,
  });
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close both "More" surfaces whenever the route changes (clicking a link
  // inside either navigates, which should dismiss the menu/sheet).
  useEffect(() => {
    setMoreOpen(false);
    setMobileMoreOpen(false);
  }, [location.pathname]);

  // Close the "More" dropdown on an outside click.
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [moreOpen]);

  // Migration 178 — the viewer's grant, and the three nav lists filtered by it.
  // Derived on every render rather than memoised: whoami lands once and the
  // lists are ~24 items, so the cost is nil and a stale memo would show links
  // the server refuses.
  const grant = effectiveGrant(state.data);
  const primaryGroups = PRIMARY_NAV_GROUPS.map((g) => visibleNav(g, grant))
    // A group whose every entry was filtered out must not leave a dangling
    // divider behind it.
    .filter((g) => g.length > 0);
  const moreNav = visibleNav(MORE_NAV, grant);
  const mobileMoreNav = visibleNav(MOBILE_MORE_NAV, grant);

  const mobileTabAllowed = (to: string): boolean => {
    const page = pageForRoute(to);
    return page === null ? true : pageAllowed(grant, page.id);
  };

  const moreActive = moreNav.some((n) =>
    location.pathname.startsWith(n.to),
  );

  // The mobile "More" tab owns every route that isn't Calendar or Members.
  const mobileMoreActive = !Array.from(MOBILE_TAB_TARGETS).some((to) =>
    location.pathname.startsWith(to),
  );

  useEffect(() => {
    let cancelled = false;
    void api
      .whoami()
      .then((data) => {
        if (cancelled) return;
        // Pin the studio timezone BEFORE the state flip that unlocks
        // <Outlet /> — every page's date/time formatting reads it via
        // `studio-tz`, so no instant may render before it's set.
        setStudioTimeZone(data.studio.timezone);
        // Pin the cancellation window too (sibling snapshot) so the calendar's
        // late-drag detection is ready before any page renders.
        setCancellationDeadlineHours(data.studio.cancellation_deadline_hours);
        setState({ loading: false, data, error: null });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        // Stale-session detection: JWT's app_metadata.studio_id no longer
        // resolves to a row in `studios` (db:reset between session start
        // and now, or admin-side studio replacement). Sign out + bounce
        // to /login with a banner so the user gets a fresh JWT.
        if (e instanceof ApiError && e.code && STALE_SESSION_CODES.has(e.code)) {
          // Phase 67 (sub-decision E) — drop the WHOLE client cache so no
          // studio's data can render under another session.
          clearCache();
          void supabase.auth.signOut().finally(() => {
            navigate(`/login?reason=stale_session&code=${e.code}`, {
              replace: true,
            });
          });
          return;
        }
        setState({ loading: false, data: null, error: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = async (): Promise<void> => {
    // Phase 67 (sub-decision E) — clear the client cache on sign-out so a
    // subsequent login can never render the prior session's data.
    clearCache();
    await supabase.auth.signOut();
    navigate("/login");
  };

  return (
    <WhoamiContext.Provider value={state}>
      <RealtimeProvider studioId={state.data?.studio.id ?? null}>
      <div className="layout">
        <header className="topbar">
          {/* The Clavis wordmark (clavis-demo): the drawn logo from Mario's kit
              (2026-09-28), the same inline SVG useclavis.ai's masthead carries,
              stroked in currentColor so it takes the chrome ink in both themes.
              A named image, so assistive tech reads "Clavis" once. */}
          <div className="topbar-brand">
            <span className="topbar-wordmark" role="img" aria-label="Clavis">
              <svg viewBox="0 0 550 100" aria-hidden="true" focusable="false">
                <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="butt" strokeLinejoin="miter">
                  <path d="M81.1 18.9 A44 44 0 1 0 81.1 81.1" />
                  <path d="M131 -10 V94 H185" />
                  <path d="M217.3 112 L255 16 L292.7 112" />
                  <path d="M322.3 -12 L360 84 L397.7 -12" />
                  <path d="M441 -10 V110" />
                  <path transform="translate(13 0)" d="M527 18 C522 9 513 6 503 6 C489 6 477 14 477 27 C477 42 492 46 504 49 C518 52 531 58 531 72 C531 86 519 94 503 94 C491 94 480 89 475 79" />
                </g>
              </svg>
            </span>
          </div>
          {state.data ? (
            <div className="topbar-studio">
              {state.data.studio.logo_url !== null ? (
                <img
                  className="topbar-studio-logo"
                  src={state.data.studio.logo_url}
                  alt={state.data.studio.name}
                />
              ) : (
                <div className="topbar-studio-name">
                  {state.data.studio.name}
                </div>
              )}
            </div>
          ) : null}
          <nav className="topnav">
            {primaryGroups.map((group, groupIndex) => (
              <Fragment key={group[0]?.to ?? groupIndex}>
                {groupIndex > 0 ? (
                  <span className="topnav-divider" aria-hidden="true" />
                ) : null}
                {group.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end ?? false}
                    className="topnav-link"
                  >
                    {n.label}
                  </NavLink>
                ))}
              </Fragment>
            ))}
            <span className="topnav-divider" aria-hidden="true" />
            <div className="nav-more" ref={moreRef}>
              <button
                type="button"
                className={`topnav-link nav-more-btn${
                  moreActive ? " active" : ""
                }${moreOpen ? " open" : ""}`}
                aria-haspopup="true"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((open) => !open)}
              >
                More <span aria-hidden="true">▾</span>
              </button>
              {moreOpen ? (
                <div className="nav-more-menu">
                  {moreNav.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      className="nav-more-item"
                      onClick={() => setMoreOpen(false)}
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </div>
          </nav>
          <div className="topbar-user">
            <VersionChip />
            {state.data ? (
              <>
                <div className="topbar-who">
                  <span className="topbar-avatar" aria-hidden="true">
                    {emailInitials(state.data.email)}
                  </span>
                  <span>{state.data.email}</span>
                </div>
                <button className="topbar-logout" onClick={() => void logout()}>
                  Logout
                </button>
              </>
            ) : (
              <span>{state.loading ? "…" : (state.error ?? "")}</span>
            )}
          </div>

          {/* Mobile-only 3-tab bar. CSS hides it above 768px, and hides the
              desktop brand/nav/user below 768px — so exactly one nav shows at
              any width. "More" opens a sheet holding everything else. */}
          <nav className="mobile-tabbar" aria-label="Primary">
            {/*
              These two tabs are hardcoded rather than mapped, so they do NOT
              inherit the filtering applied to the nav arrays above — filtering
              only those would leave a denied Calendar one tap away on a phone.
              Gated individually against the same grant.
            */}
            {mobileTabAllowed("/calendar") ? (
              <NavLink to="/calendar" className="mobile-tab">
                Calendar
              </NavLink>
            ) : null}
            {mobileTabAllowed("/members") ? (
              <NavLink to="/members" className="mobile-tab">
                Members
              </NavLink>
            ) : null}
            <button
              type="button"
              className={`mobile-tab mobile-tab-more${
                mobileMoreActive ? " active" : ""
              }${mobileMoreOpen ? " open" : ""}`}
              aria-haspopup="true"
              aria-expanded={mobileMoreOpen}
              onClick={() => setMobileMoreOpen((open) => !open)}
            >
              More <span aria-hidden="true">▾</span>
            </button>
          </nav>

          {mobileMoreOpen ? (
            <div className="mobile-more-sheet" role="menu">
              <div className="mobile-more-grid">
                {mobileMoreNav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className="mobile-more-item"
                    onClick={() => setMobileMoreOpen(false)}
                  >
                    {n.label}
                  </NavLink>
                ))}
              </div>
              <div className="mobile-more-footer">
                {state.data ? (
                  <>
                    <span className="mobile-more-user">
                      {state.data.email} · {state.data.studio.name}
                    </span>
                    <button onClick={() => void logout()}>Logout</button>
                  </>
                ) : (
                  <span>{state.loading ? "…" : (state.error ?? "")}</span>
                )}
              </div>
            </div>
          ) : null}
        </header>
        {mobileMoreOpen ? (
          <div
            className="mobile-more-backdrop"
            aria-hidden="true"
            onClick={() => setMobileMoreOpen(false)}
          />
        ) : null}
        <main className="main">
          {/* Pages render only once whoami resolved — the studio timezone is
              pinned in that handler, and a page that painted earlier would
              format its first batch of times in the default zone.

              Migration 178 — the ROUTE guard. Hiding a nav link stops a click,
              not a typed URL or a bookmark, so the page itself must refuse to
              render. The server refuses its data independently (`page-access.ts`);
              this is what stops a half-drawn page from flashing its chrome and
              then erroring, and what makes the redirect land somewhere useful.

              Ordered AFTER the loading gate on purpose: `grant` is `null`
              (unrestricted) until whoami lands, so guarding first would bounce
              every deep link to `/` for one frame on every page load. */}
          {state.loading ? null : (
            <RouteGuard grant={grant} resolved={state.data?.page_access_resolved ?? true}>
              <Outlet />
            </RouteGuard>
          )}
        </main>
      </div>
      </RealtimeProvider>
    </WhoamiContext.Provider>
  );
}

/** "mario.kaspers@icloud.com" → "MK". Single-word local parts fall back to
 * their first two letters. */
function emailInitials(email: string): string {
  const local = email.split("@")[0] ?? email;
  const words = local.split(/[._\-+]+/).filter((w) => w.length > 0);
  const initials =
    words.length >= 2
      ? words
          .slice(0, 2)
          .map((w) => w.charAt(0))
          .join("")
      : local.slice(0, 2);
  return initials.toUpperCase();
}

function VersionChip(): JSX.Element {
  // `LAYER_PUB_VERSION` is injected from `git rev-parse --short HEAD` at
  // build time (vite.config.ts). The "MVP" fallback covers only builds where
  // git is absent and the value falls back to "unknown" (or is unset) — the
  // date/time beside it already updates per deploy.
  const rawVersion = import.meta.env["LAYER_PUB_VERSION"] as string | undefined;
  const version =
    rawVersion && rawVersion !== "unknown" && rawVersion !== "dev"
      ? rawVersion
      : "MVP";
  const commitDateStr =
    (import.meta.env["LAYER_PUB_COMMIT_DATE"] as string) ??
    new Date().toISOString();
  const buildTimeStr =
    (import.meta.env["LAYER_PUB_BUILD_TIME"] as string) ??
    new Date().toISOString();
  const commitDate = new Date(commitDateStr);
  const builtAt = new Date(buildTimeStr);
  const fmt = (d: Date): string =>
    d.toLocaleString("en-GB", {
      timeZone: studioTimeZone(),
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  // Collapsed to the commit glyph — the build identity is reference data, not
  // something to read on every page. Hover reveals it transiently; a click
  // pins it open so it survives the pointer leaving.
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      className={`version-chip${open ? " open" : ""}`}
      aria-expanded={open}
      aria-label={`Build version ${version}, committed ${fmt(commitDate)}`}
      title={`commit ${version}\ncommit date ${commitDate.toISOString()}\ndev started ${builtAt.toISOString()}`}
      onClick={() => setOpen((v) => !v)}
    >
      {/* The "v" is the collapsed state's whole face AND the expanded string's
          first token — so expanding reads "v <sha> · <date>" continuously
          rather than pushing a separate icon in front of a second "v". */}
      <span className="version-chip-mark" aria-hidden="true">
        v
      </span>
      <span className="version-chip-detail">
        <span className="version-chip-label">{version}</span>
        <span className="version-chip-sep">·</span>
        <span className="version-chip-date">{fmt(commitDate)}</span>
      </span>
    </button>
  );
}
